/**
 * Porte des écrans du niveau PROJET — lot NAV 0.5.x (registre n° 11 ; A2 § 2.2, constat 7).
 *
 * Deux situations rendaient une « cascade de 403 » (une douzaine de lectures refusées, des blocs
 * « indisponible : la lecture n'a pas répondu ») au lieu de dire ce qui se passe :
 *  - l'adresse désigne un projet dont on n'est PAS membre (`?projet=` hors de ses projets : lien
 *    partagé, notification, recherche) — l'écran dit « Vous n'êtes pas membre de ce projet » et
 *    propose de DEMANDER l'accès (ou de le rejoindre, pour l'administrateur de la plateforme) ;
 *  - le projet ouvert est ARCHIVÉ — la passerelle refuse toute lecture d'appartenance sur un projet
 *    archivé (`WorkspaceMembershipGuard`, `w.status = 'active'`) : l'écran le dit et propose d'en
 *    choisir un autre. Constaté en base le 2026-10-05 : la « cascade » du parcours A2 sur
 *    `ws-f79bf518-9a1` venait d'un projet ARCHIVÉ dont les trois comptes étaient membres.
 *
 * La porte n'agit qu'au niveau Projet et une fois la liste des espaces connue ; ailleurs, et
 * pendant le chargement, l'écran demandé est rendu tel quel.
 *
 * AUD-CRAWL-P9 (audit du 2026-10-09) : un `?projet=` ABSENT de la liste COMPLÈTE de l'organisation
 * (inconnu, supprimé, ou d'une autre organisation) n'est pas « un projet de votre organisation sans
 * rôle » : la porte le dit INTROUVABLE, sans demande d'accès (elle échouerait en 404) ni appartenance
 * affirmée. Tant que la liste n'est pas complète (voir `projetsDeLOrganisation`), l'absence ne prouve
 * rien : la porte garde « non-membre » et la demande d'accès ; la passerelle tranche, et son 404 est dit
 * (`nonMembre.introuvable`).
 *
 * D-17 (suites du même audit) : pour qui ne voit que ses projets (propriétaire, lecteur, administration
 * d'organisation), la porte DEMANDE à la passerelle si le projet existe dans l'organisation
 * (`demandesAccesApi.existence` : même 404 pour « inexistant » et « autre organisation ») : 404 → introuvable ;
 * 200 → non membre, appartenance affirmée, « Demander l'accès » ; une autre panne ne tranche rien et garde
 * l'écran neutre. L'administrateur de la plateforme, qui voit la liste complète, ne la lit pas.
 */
import type { JSX, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { ApiError, type Workspace } from '@/api/client'
import { demandesAccesApi } from '@/api/demandesAccesApi'
import { LoadingState } from '@/components/ErrorState'
import { useT } from '@/i18n/language'
import type { Session } from '@/auth/session'
import { useWorkspace } from '@/hooks/useWorkspace'
import type { Niveau } from '@/lib/nav'
import { ProjetArchive, ProjetIntrouvable, ProjetNonMembre } from '@/screens/projet/ProjetNonMembre'

/** Décision pure de la porte, testée à part. */
export function decisionPorte({ niveau, projetDemande, accessibles, organisation = null, actifArchive, chargement, erreur }: {
  niveau: Niveau
  projetDemande: string | null
  accessibles: readonly string[]
  /** Tous les projets de l'organisation, membre ou non, quand la liste est COMPLÈTE ; `null` : liste partielle ou inconnue. */
  organisation?: readonly string[] | null
  actifArchive: boolean
  chargement: boolean
  erreur: boolean
}): 'ecran' | 'non-membre' | 'introuvable' | 'archive' {
  if (niveau !== 'projet' || chargement || erreur) return 'ecran'
  if (projetDemande && !accessibles.includes(projetDemande)) {
    return organisation && !organisation.includes(projetDemande) ? 'introuvable' : 'non-membre'
  }
  if (actifArchive) return 'archive'
  return 'ecran'
}

/**
 * Les projets de l'ORGANISATION, si la liste du porteur les contient tous ; sinon `null` (inconnus).
 *
 * `GET /workspaces` ne renvoie à un porteur ordinaire QUE ses projets (`WorkspaceController#getWorkspaces` :
 * « SEULS les espaces membres ; tout, pour l'admin plateforme ») : pour lui, un projet absent de la liste
 * peut être un projet de son organisation où il n'a aucun rôle — le cas pour lequel la demande d'accès
 * existe. Déclarer « introuvable » sur cette seule absence la rendrait inaccessible à tout le monde.
 * La liste n'est complète que pour qui voit toute l'organisation : l'administrateur de la plateforme, ou
 * quiconque y lit un projet SANS y avoir de rôle (la passerelle ne le liste pas autrement).
 */
function projetsDeLOrganisation(
  session: Session, listes: readonly Workspace[] | undefined, accessibles: readonly string[],
): readonly string[] | null {
  if (!Array.isArray(listes)) return null
  const ids = listes.map((w) => w.workspace_id)
  const complete = session.roles.includes('admin') || ids.some((id) => !accessibles.includes(id))
  return complete ? ids : null
}

export function PorteProjet({ session, niveau, children }: {
  session: Session
  niveau: Niveau
  children: ReactNode
}): JSX.Element {
  const { search } = useLocation()
  const espaces = useWorkspace()
  // Défensif : des montages partiels (doubles de test) ne fournissent pas toutes les listes.
  const accessibles = espaces.accessibles ?? []
  const aRejoindre = espaces.aRejoindre ?? []
  const { active, isLoading, error } = espaces
  // Sans liste connue (double partiel, pas encore chargée), aucune décision n'est prise.
  const listeConnue = Array.isArray(espaces.accessibles)
  const projetDemande = new URLSearchParams(search).get('projet')
  const idsAccessibles = accessibles.map((w) => w.workspace_id)
  // Liste de l'ORGANISATION quand elle est complète ; sinon (porteur ordinaire, double partiel) un projet
  // hors de mes projets reste traité en « non membre », comme avant.
  const organisation = projetsDeLOrganisation(session, espaces.workspaces, idsAccessibles)
  const decision = decisionPorte({
    niveau, projetDemande, accessibles: idsAccessibles, organisation,
    actifArchive: active?.status === 'archived', chargement: Boolean(isLoading) || !listeConnue, erreur: Boolean(error),
  })
  if (decision === 'introuvable' && projetDemande) return <ProjetIntrouvable workspaceId={projetDemande} />
  if (decision === 'non-membre' && projetDemande) {
    const aRejoindreCible = aRejoindre.find((w) => w.workspace_id === projetDemande) ?? null
    // Listé par une liste complète : son appartenance à l'organisation est CERTAINE, rien à demander.
    if (organisation !== null) {
      return <ProjetNonMembre workspaceId={projetDemande} session={session} aRejoindre={aRejoindreCible} dansOrganisation />
    }
    // Liste partielle : l'absence ne prouve rien, la passerelle dit si le projet existe dans l'organisation (D-17).
    return <PorteExistence workspaceId={projetDemande} session={session} aRejoindre={aRejoindreCible} />
  }
  if (decision === 'archive' && active) return <ProjetArchive projet={active} />
  return <>{children}</>
}

/**
 * D-17 : la lecture d'existence, montée SEULEMENT quand la porte en a besoin (liste partielle, projet hors de mes
 * projets) — la porte elle-même ne lit rien d'autre que la liste des espaces.
 */
function PorteExistence({ workspaceId, session, aRejoindre }: {
  workspaceId: string
  session: Session
  aRejoindre: Workspace | null
}): JSX.Element {
  const { t } = useT()
  const existence = useQuery({
    // Portée : l'organisation de l'appelant ET le projet (la réponse dépend des deux).
    queryKey: ['existence-projet', session.organisationId ?? null, workspaceId],
    queryFn: () => demandesAccesApi.existence(workspaceId),
    staleTime: 30_000,
    retry: false,
  })
  if (existence.error instanceof ApiError && existence.error.status === 404) return <ProjetIntrouvable workspaceId={workspaceId} />
  if (existence.isPending) {
    return (
      <div data-testid="porte-projet-verification">
        <LoadingState label={t('porteProjet.verification', 'Vérification du projet dans votre organisation…')} />
      </div>
    )
  }
  // Déclaré existant par la passerelle : appartenance CERTAINE ; une autre panne ne tranche rien (non affirmée).
  return <ProjetNonMembre workspaceId={workspaceId} session={session} aRejoindre={aRejoindre}
                          dansOrganisation={existence.data?.exists === true} />
}
